import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as callSubStatusesApi from './call-sub-statuses.api';

import type {
  CallSubStatusListQuery,
  CreateCallSubStatusRequest,
  UpdateCallSubStatusRequest,
} from '@utazzo/shared';

const QK = {
  list: (query: CallSubStatusListQuery) => ['call-sub-statuses', 'list', query] as const,
  detail: (id: string) => ['call-sub-statuses', 'detail', id] as const,
};

export const useCallSubStatusesList = (query: CallSubStatusListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => callSubStatusesApi.listCallSubStatuses(query),
    staleTime: 30_000,
  });

/**
 * Cross-cache invalidation for sub-status mutations. LeadFollowUp
 * embeds `callSubStatusId` (and the follow-up history is rendered
 * from cached lead detail), so when the master changes those
 * embedded copies on cached lead rows should reload.
 */
const invalidateSubStatusFanout = (qc: ReturnType<typeof useQueryClient>): void => {
  qc.invalidateQueries({ queryKey: ['call-sub-statuses', 'list'] });
  qc.invalidateQueries({ queryKey: ['leads', 'list'] });
  qc.invalidateQueries({ queryKey: ['leads', 'detail'] });
};

export const useCreateCallSubStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateCallSubStatusRequest) =>
      callSubStatusesApi.createCallSubStatus(payload),
    onSuccess: (css) => {
      invalidateSubStatusFanout(qc);
      toast.success(`Added ${css.name}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add call sub status'),
  });
};

export const useUpdateCallSubStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateCallSubStatusRequest }) =>
      callSubStatusesApi.updateCallSubStatus(id, payload),
    onSuccess: (css) => {
      invalidateSubStatusFanout(qc);
      qc.invalidateQueries({ queryKey: QK.detail(css.id) });
      toast.success('Call sub status updated');
    },
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to update call sub status'),
  });
};

export const useDeleteCallSubStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => callSubStatusesApi.deleteCallSubStatus(id),
    onSuccess: () => {
      invalidateSubStatusFanout(qc);
      toast.success('Call sub status deleted');
    },
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to delete call sub status'),
  });
};

export const useBulkDeleteCallSubStatuses = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => callSubStatusesApi.bulkDeleteCallSubStatuses(ids),
    onSuccess: () => invalidateSubStatusFanout(qc),
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to delete call sub statuses'),
  });
};

export const useBulkSetCallSubStatusesStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, isActive }: { ids: string[]; isActive: boolean }) =>
      callSubStatusesApi.bulkSetCallSubStatusesStatus(ids, isActive),
    onSuccess: () => invalidateSubStatusFanout(qc),
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to update call sub status'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
